/**
 * Page de gestion des membres - Administration des comptes parents
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService, enfantService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { notify } from '../components/NotificationContainer';
import { prompt } from '../components/PromptContainer';
import { confirm } from '../components/ConfirmContainer';
import logger from '../utils/logger';

const GestionMembres = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEnfants, setUserEnfants] = useState([]);
  const [enfantsRecherche, setEnfantsRecherche] = useState([]);
  const [loadingEnfantsRecherche, setLoadingEnfantsRecherche] = useState(false);
  const debounceRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'details', 'password', 'enfants', 'delete'
  const [searchEnfant, setSearchEnfant] = useState('');

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate, currentPage, searchTerm, roleFilter, activeFilter]);

  // Réinitialiser la page à 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, activeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);

      // OPTIMISATION: Utiliser le filtrage backend au lieu du filtrage client
      // Construire les paramètres de requête pour le backend
      const params = {
        page: currentPage,
      };

      // Ajouter les paramètres de filtrage s'ils sont définis
      if (searchTerm) {
        params.search = searchTerm;
      }

      if (roleFilter) {
        params.role = roleFilter;
      }

      if (activeFilter !== 'all') {
        params.is_active = activeFilter === 'active' ? 'true' : 'false';
      }

      // Charger les utilisateurs avec pagination et filtrage backend
      const usersResp = await userService.getAll(params);

      // Gérer la réponse paginée
      if (usersResp.data.results) {
        // Réponse paginée
        const usersData = usersResp.data.results;
        setUsers(usersData);
        setFilteredUsers(usersData);
        setTotalCount(usersResp.data.count);

        // PAGE_SIZE défini dans backend/config/settings.py
        const PAGE_SIZE = 20;
        const computedPages = Math.ceil(usersResp.data.count / PAGE_SIZE);
        setTotalPages(computedPages);

        logger.log(`[GestionMembres] Chargé ${usersData.length} utilisateurs sur ${usersResp.data.count} (page ${currentPage}/${computedPages})`);
      } else {
        // Réponse non paginée (fallback)
        const usersData = Array.isArray(usersResp.data) ? usersResp.data : [];
        setUsers(usersData);
        setFilteredUsers(usersData);
        setTotalCount(usersData.length);
        setTotalPages(1);
      }
    } catch (error) {
      logger.error('Erreur lors du chargement des données:', error);
      notify.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // OPTIMISATION: Le filtrage est maintenant géré côté backend dans loadData()
  // L'ancien useEffect de filtrage côté client a été supprimé

  // Ouvrir le modal avec les détails d'un utilisateur
  const openModal = async (type, userItem) => {
    setSelectedUser(userItem);
    setModalType(type);
    setShowModal(true);

    if (type === 'enfants') {
      try {
        const [enfantsResp, rechercheResp] = await Promise.all([
          userService.getEnfants(userItem.id),
          enfantService.search(''),
        ]);
        setUserEnfants(enfantsResp.data);
        const rechercheData = Array.isArray(rechercheResp.data)
          ? rechercheResp.data
          : rechercheResp.data.results || [];
        setEnfantsRecherche(rechercheData);
      } catch (error) {
        logger.error('Erreur lors du chargement des enfants:', error);
        setUserEnfants([]);
        setEnfantsRecherche([]);
      }
    }
  };

  // Fermer le modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setUserEnfants([]);
    setEnfantsRecherche([]);
    setModalType('');
    setSearchEnfant('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  // Supprimer un compte
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    const confirmation = await prompt.askRequired(
      `ATTENTION : Cette action est IRRÉVERSIBLE !\n\nTapez "SUPPRIMER" pour confirmer la suppression du compte de ${selectedUser.first_name} ${selectedUser.last_name}`,
      'Tapez SUPPRIMER',
      'Confirmation de suppression'
    );

    if (confirmation !== 'SUPPRIMER') {
      notify.info('Suppression annulée');
      return;
    }

    try {
      await userService.delete(selectedUser.id);
      notify.success('Compte supprimé avec succès');
      closeModal();
      loadData();
    } catch (error) {
      logger.error('Erreur lors de la suppression:', error);
      notify.error('Erreur lors de la suppression du compte');
    }
  };

  // Activer/désactiver un compte
  const handleToggleActive = async (userItem) => {
    try {
      await userService.toggleActive(userItem.id);
      notify.success(`Compte ${userItem.is_active ? 'désactivé' : 'activé'} avec succès`);
      loadData();
    } catch (error) {
      logger.error('Erreur lors de la modification du statut:', error);
      notify.error('Erreur lors de la modification du statut');
    }
  };

  // Réinitialiser le mot de passe
  const handleResetPassword = async () => {
    if (!selectedUser) return;

    const newPassword = await prompt.askPassword(
      `Nouveau mot de passe pour ${selectedUser.first_name} ${selectedUser.last_name}:\n\n(Minimum 8 caractères)`,
      'Minimum 8 caractères',
      'Réinitialiser le mot de passe'
    );

    if (!newPassword) return;

    if (newPassword.length < 8) {
      notify.warning('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    try {
      await userService.resetPassword(selectedUser.id, newPassword);
      notify.success('Mot de passe réinitialisé avec succès');
      closeModal();
    } catch (error) {
      logger.error('Erreur lors de la réinitialisation:', error);
      notify.error(error.response?.data?.error || 'Erreur lors de la réinitialisation du mot de passe');
    }
  };

  // Affecter un enfant à un compte
  const handleAffecterEnfant = async (enfantId) => {
    if (!selectedUser) return;

    // Trouver l'enfant pour afficher ses informations
    const enfant = enfantsRecherche.find((e) => e.id === enfantId);
    if (!enfant) return;

    // Message de confirmation différent selon si l'enfant a déjà un parent ou non
    let confirmMessage = '';
    if (enfant.parent) {
      confirmMessage = `Voulez-vous réaffecter ${enfant.prenom} ${enfant.nom} au compte de ${selectedUser.first_name} ${selectedUser.last_name} ?\n\nCet enfant est actuellement associé à : ${enfant.parent_name}`;
      if (!await confirm.warning(confirmMessage, 'Réaffecter l\'enfant')) {
        return;
      }
    } else {
      confirmMessage = `Voulez-vous affecter ${enfant.prenom} ${enfant.nom} au compte de ${selectedUser.first_name} ${selectedUser.last_name} ?`;
      if (!await confirm.info(confirmMessage, 'Affecter l\'enfant')) {
        return;
      }
    }

    try {
      await userService.affecterEnfant(selectedUser.id, enfantId);
      notify.success('Enfant affecté avec succès');
      // Recharger les enfants de l'utilisateur et la liste de recherche
      const [enfantsResp, rechercheResp] = await Promise.all([
        userService.getEnfants(selectedUser.id),
        enfantService.search(searchEnfant),
      ]);
      setUserEnfants(enfantsResp.data);
      const rechercheData = Array.isArray(rechercheResp.data)
        ? rechercheResp.data
        : rechercheResp.data.results || [];
      setEnfantsRecherche(rechercheData);
      loadData();
    } catch (error) {
      logger.error('Erreur lors de l\'affectation:', error);
      notify.error('Erreur lors de l\'affectation de l\'enfant');
    }
  };

  // Désaffecter un enfant d'un compte (sans supprimer)
  const handleDesaffecterEnfant = async (enfantId) => {
    if (!selectedUser) return;

    if (!await confirm.warning('Voulez-vous désaffecter cet enfant du compte ? L\'enfant sera retiré mais pas supprimé.', 'Désaffecter l\'enfant')) {
      return;
    }

    try {
      await userService.desaffecterEnfant(selectedUser.id, enfantId);
      notify.success('Enfant désaffecté avec succès');
      // Recharger les enfants de l'utilisateur et la liste de recherche
      const [enfantsResp, rechercheResp] = await Promise.all([
        userService.getEnfants(selectedUser.id),
        enfantService.search(searchEnfant),
      ]);
      setUserEnfants(enfantsResp.data);
      const rechercheData = Array.isArray(rechercheResp.data)
        ? rechercheResp.data
        : rechercheResp.data.results || [];
      setEnfantsRecherche(rechercheData);
      loadData();
    } catch (error) {
      logger.error('Erreur lors de la désaffectation:', error);
      notify.error('Erreur lors de la désaffectation de l\'enfant');
    }
  };

  // Supprimer définitivement un enfant
  const handleSupprimerEnfant = async (enfantId) => {
    if (!selectedUser) return;

    if (!await confirm.danger('ATTENTION : Voulez-vous SUPPRIMER DÉFINITIVEMENT cet enfant ?\n\nCette action est IRRÉVERSIBLE et supprimera également toutes ses inscriptions.', 'Supprimer définitivement')) {
      return;
    }

    try {
      await userService.supprimerEnfant(selectedUser.id, enfantId);
      notify.success('Enfant supprimé définitivement');
      // Recharger les enfants de l'utilisateur
      const response = await userService.getEnfants(selectedUser.id);
      setUserEnfants(response.data);
      loadData();
    } catch (error) {
      logger.error('Erreur lors de la suppression:', error);
      notify.error('Erreur lors de la suppression de l\'enfant');
    }
  };

  // Recherche d'enfants côté serveur avec debounce 300ms
  useEffect(() => {
    if (modalType !== 'enfants') return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoadingEnfantsRecherche(true);
      try {
        const response = await enfantService.search(searchEnfant);
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.results || [];
        setEnfantsRecherche(data);
      } catch (error) {
        logger.error('Erreur lors de la recherche d\'enfants:', error);
        setEnfantsRecherche([]);
      } finally {
        setLoadingEnfantsRecherche(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchEnfant, modalType]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            Gestion des Membres
          </h1>

          {/* Filtres et recherche */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher
              </label>
              <input
                type="text"
                placeholder="Nom, prénom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rôle
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">Tous les rôles</option>
                <option value="PARENT">Parents</option>
                <option value="ADMIN">Administrateurs</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="all">Tous</option>
                <option value="active">Actifs</option>
                <option value="inactive">Inactifs</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tableau des utilisateurs */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom complet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Téléphone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enfants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((userItem) => (
                <tr key={userItem.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {userItem.first_name} {userItem.last_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{userItem.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{userItem.phone || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded inline-flex items-center gap-1 ${
                        userItem.role === 'ADMIN'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {userItem.role === 'ADMIN' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {userItem.role === 'ADMIN' ? 'Administrateur' : 'Parent'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => openModal('enfants', userItem)}
                      className="text-sm text-primary-600 hover:text-primary-900 font-medium"
                    >
                      {userItem.nb_enfants ?? 0} enfant(s)
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        userItem.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {userItem.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-2">
                      {/* Bouton Détails - Toujours accessible */}
                      <button
                        onClick={() => openModal('details', userItem)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                        title="Voir les détails"
                      >
                        Détails
                      </button>

                      {/* Bouton Mot de passe - Désactivé pour les admins */}
                      <button
                        onClick={() => userItem.role !== 'ADMIN' && openModal('password', userItem)}
                        disabled={userItem.role === 'ADMIN'}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          userItem.role === 'ADMIN'
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                        }`}
                        title={
                          userItem.role === 'ADMIN'
                            ? 'Les administrateurs doivent changer leur mot de passe via leur profil'
                            : 'Réinitialiser le mot de passe'
                        }
                      >
                        Mot de passe
                      </button>

                      {/* Bouton Activer/Désactiver - Désactivé pour les admins */}
                      <button
                        onClick={() => userItem.role !== 'ADMIN' && handleToggleActive(userItem)}
                        disabled={userItem.role === 'ADMIN'}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          userItem.role === 'ADMIN'
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : userItem.is_active
                            ? 'bg-orange-600 hover:bg-orange-700 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                        title={
                          userItem.role === 'ADMIN'
                            ? 'Les comptes administrateurs ne peuvent pas être désactivés'
                            : userItem.is_active
                            ? 'Désactiver'
                            : 'Activer'
                        }
                      >
                        {userItem.is_active ? 'Désactiver' : 'Activer'}
                      </button>

                      {/* Bouton Supprimer - Désactivé pour les admins */}
                      <button
                        onClick={() => userItem.role !== 'ADMIN' && openModal('delete', userItem)}
                        disabled={userItem.role === 'ADMIN'}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          userItem.role === 'ADMIN'
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                        title={
                          userItem.role === 'ADMIN'
                            ? 'Les comptes administrateurs ne peuvent pas être supprimés via cette interface'
                            : 'Supprimer le compte'
                        }
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucun membre trouvé
            </div>
          )}
        </div>

        {/* Pagination - Toujours affichée */}
        <div className="mt-6 bg-white shadow-sm border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Info pagination */}
            <div className="text-sm text-gray-700">
              {totalCount === 0 ? (
                <span>Aucun membre</span>
              ) : totalPages === 1 ? (
                <span>
                  {totalCount} membre{totalCount > 1 ? 's' : ''} • Page 1/1
                </span>
              ) : (
                <span>
                  Page {currentPage} sur {totalPages} • {totalCount} membre{totalCount > 1 ? 's' : ''} au total
                </span>
              )}
            </div>

            {/* Boutons de navigation - toujours visibles */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === 1 || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                title="Première page"
              >
                ⏮
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === 1 || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                title="Page précédente"
              >
                ←
              </button>

              {/* Indicateur de page actuelle */}
              <div className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium min-w-[80px] text-center">
                {currentPage} / {totalPages}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === totalPages || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                title="Page suivante"
              >
                →
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || loading}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === totalPages || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                title="Dernière page"
              >
                ⏭
              </button>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="mt-6 bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{users.length}</div>
              <div className="text-sm text-gray-600">Total membres</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {users.filter((u) => u.role === 'PARENT').length}
              </div>
              <div className="text-sm text-gray-600">Parents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {users.filter((u) => u.role === 'ADMIN').length}
              </div>
              <div className="text-sm text-gray-600">Administrateurs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {users.filter((u) => u.is_active).length}
              </div>
              <div className="text-sm text-gray-600">Comptes actifs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {modalType === 'details' && 'Détails du compte'}
                  {modalType === 'password' && 'Réinitialiser le mot de passe'}
                  {modalType === 'enfants' && 'Gérer les enfants'}
                  {modalType === 'delete' && 'Supprimer le compte'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Contenu du modal - Détails */}
              {modalType === 'details' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nom complet</label>
                    <p className="mt-1 text-lg">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-lg">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                    <p className="mt-1 text-lg">{selectedUser.phone || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Adresse</label>
                    <p className="mt-1 text-lg">{selectedUser.address || 'Non renseignée'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rôle</label>
                    <p className="mt-1 text-lg">
                      {selectedUser.role === 'ADMIN' ? 'Administrateur' : 'Parent'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Date d'inscription
                    </label>
                    <p className="mt-1 text-lg">
                      {new Date(selectedUser.date_joined).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Statut</label>
                    <p className="mt-1 text-lg">
                      {selectedUser.is_active ? 'Actif' : 'Inactif'}
                    </p>
                  </div>
                </div>
              )}

              {/* Contenu du modal - Mot de passe */}
              {modalType === 'password' && (
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Vous êtes sur le point de réinitialiser le mot de passe de{' '}
                    <strong>
                      {selectedUser.first_name} {selectedUser.last_name}
                    </strong>
                    .
                  </p>
                  <p className="text-sm text-gray-600">
                    Un nouveau mot de passe vous sera demandé. Assurez-vous de le communiquer à
                    l'utilisateur de manière sécurisée.
                  </p>
                  <div className="flex space-x-4">
                    <button
                      onClick={handleResetPassword}
                      className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Réinitialiser
                    </button>
                    <button
                      onClick={closeModal}
                      className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* Contenu du modal - Enfants */}
              {modalType === 'enfants' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      Enfants de {selectedUser.first_name} {selectedUser.last_name}
                    </h3>
                    {userEnfants.length > 0 ? (
                      <div className="space-y-2">
                        {userEnfants.map((enfant) => (
                          <div
                            key={enfant.id}
                            className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                          >
                            <div>
                              <p className="font-medium">
                                {enfant.prenom} {enfant.nom}
                              </p>
                              <p className="text-sm text-gray-600">
                                {new Date(enfant.date_naissance).toLocaleDateString('fr-FR')} (
                                {enfant.age} ans)
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleDesaffecterEnfant(enfant.id)}
                                className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                                title="Désaffecter sans supprimer"
                              >
                                Désaffecter
                              </button>
                              <button
                                onClick={() => handleSupprimerEnfant(enfant.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                                title="Supprimer définitivement"
                              >
                                Supprimer
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">Aucun enfant associé</p>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-2">Affecter un enfant</h3>

                    {/* Barre de recherche côté serveur avec debounce */}
                    <div className="mb-3 relative">
                      <input
                        type="text"
                        placeholder="Rechercher un enfant (nom, prénom)..."
                        value={searchEnfant}
                        onChange={(e) => setSearchEnfant(e.target.value)}
                        className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                      {loadingEnfantsRecherche && (
                        <span className="absolute right-3 top-2.5 text-gray-400 text-sm">...</span>
                      )}
                    </div>

                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {enfantsRecherche
                        .filter((e) => e.parent !== selectedUser.id)
                        .map((enfant) => (
                          <div
                            key={enfant.id}
                            className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                          >
                            <div>
                              <p className="font-medium">
                                {enfant.prenom} {enfant.nom}
                              </p>
                              <p className="text-sm text-gray-600">
                                {new Date(enfant.date_naissance).toLocaleDateString('fr-FR')} (
                                {enfant.age} ans)
                                {enfant.parent_name && (
                                  <span className="ml-2 text-blue-600">
                                    - Actuellement: {enfant.parent_name}
                                  </span>
                                )}
                                {!enfant.parent && (
                                  <span className="ml-2 text-gray-500 italic">
                                    - Sans parent
                                  </span>
                                )}
                              </p>
                            </div>
                            <button
                              onClick={() => handleAffecterEnfant(enfant.id)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                            >
                              Affecter
                            </button>
                          </div>
                        ))}
                      {!loadingEnfantsRecherche &&
                        enfantsRecherche.filter((e) => e.parent !== selectedUser.id).length === 0 && (
                        <p className="text-gray-500 italic text-center py-4">
                          {searchEnfant ? 'Aucun enfant trouvé' : 'Aucun enfant disponible'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Contenu du modal - Suppression */}
              {modalType === 'delete' && (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-medium">⚠️ ATTENTION : Action irréversible</p>
                    <p className="text-red-700 mt-2">
                      Vous êtes sur le point de supprimer définitivement le compte de{' '}
                      <strong>
                        {selectedUser.first_name} {selectedUser.last_name}
                      </strong>
                      .
                    </p>
                    <p className="text-red-700 mt-2">
                      Cette action supprimera également tous les enfants et inscriptions associés à
                      ce compte.
                    </p>
                  </div>
                  <div className="flex space-x-4">
                    <button
                      onClick={handleDeleteUser}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Supprimer définitivement
                    </button>
                    <button
                      onClick={closeModal}
                      className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default GestionMembres;
